import { ErrorAlert, ListItem, LoadingAlert, TokenIcon } from '@/components';
import { useNews } from '@/components/providers';
import { NewsInterface } from '@/interfaces';
import { logError, logInfo } from '@/utils/logger';
import { NEWS } from '@/utils/strings';
import DOMPurify from 'dompurify';
import Image from 'next/image';
import Link from 'next/link';
import { FC, memo, useMemo } from 'react';
const unescapeHtmlEntities = (str: string): string => {
  return str
    .replace(/&/g, '&')
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/"/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#8230;/g, '...');
};
interface NewsListProps {
  limit?: number;
  includeMetadata?: boolean;
}
interface NewsListItemProps {
  newsItem: NewsInterface;
  includeMetadata: boolean;
}
const NewsListItem = memo(
  ({ newsItem, includeMetadata }: NewsListItemProps) => {
    const renderIcon = () => {
      if (newsItem.image_url) {
        return (
          <div className="w-9 h-9 rounded-full overflow-hidden relative">
            <Image
              src={newsItem.image_url}
              alt={`${newsItem.source_name} icon`}
              fill
              className="object-cover"
            />
          </div>
        );
      }
      return <TokenIcon tokenSymbol="default" alt="Default news icon" />;
    };
    const formatDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (e) {
        return dateString;
      }
    };
    const truncateTitle = (title: string, maxLength: number = 150) => {
      if (title.length <= maxLength) return title;
      const truncated = title.substr(0, maxLength);
      const lastSpace = truncated.lastIndexOf(' ');
      const truncatedWithoutLastWord = truncated.substr(0, lastSpace);
      const lastWord = title.split(' ').pop();
      return `${truncatedWithoutLastWord}... ${lastWord}`;
    };
    const ensureTitleEndsWithProperPunctuation = (title: string): string => {
      const sanitizedTitle = unescapeHtmlEntities(
        DOMPurify.sanitize(title.trim()),
      );
      const trimmedTitle = sanitizedTitle.trim();
      const nonPeriodEndingPunctuation = ['.', '?', '!', '...'];
      const endsWithValidPunctuation = nonPeriodEndingPunctuation.some((mark) =>
        trimmedTitle.endsWith(mark),
      );
      if (endsWithValidPunctuation) {
        return trimmedTitle;
      }
      return `${trimmedTitle}.`;
    };
    if (!newsItem) {
      return null;
    }
    return (
      <Link
        href={newsItem.news_url}
        target="_blank"
        rel="noopener noreferrer"
        className="no-underline"
      >
        <ListItem
          mainText={ensureTitleEndsWithProperPunctuation(
            truncateTitle(
              unescapeHtmlEntities(DOMPurify.sanitize(newsItem.title)),
            ),
          )}
          subText={includeMetadata ? formatDate(newsItem.date) : undefined}
          className="cursor-pointer"
          leftIcon={renderIcon()}
          shouldMiddleAlignIconAndText={false}
        />
      </Link>
    );
  },
);
NewsListItem.displayName = 'NewsListItem';
const NewsList: FC<NewsListProps> = ({
  limit = 100,
  includeMetadata = false,
}) => {
  const { news, isLoading, error } = useNews(limit);
  const content = useMemo(() => {
    if (isLoading) {
      logInfo('Loading news, but none found yet', 'NewsList');
      return (
        <LoadingAlert
          title={NEWS.LOADING_TITLE}
          description={NEWS.LOADING_DESCRIPTION}
        />
      );
    }
    if (error) {
      logError('Error loading news', 'NewsList', { error });
      return (
        <ErrorAlert
          title={NEWS.ERROR_LOADING_TITLE}
          description={NEWS.ERROR_LOADING_DESCRIPTION}
        />
      );
    }
    if (news.length === 0) {
      return (
        <ErrorAlert
          title={NEWS.EMPTY_NEWS_TITLE}
          description={NEWS.EMPTY_NEWS_DESCRIPTION}
        />
      );
    }
    return news.map((newsItem, index) => (
      <NewsListItem
        key={`news-${index}-${newsItem.news_url}`}
        newsItem={newsItem}
        includeMetadata={includeMetadata}
      />
    ));
  }, [news, isLoading, error, includeMetadata]);
  return <div className="news-list flex flex-col gap-sm">{content}</div>;
};
export default NewsList;
